import { User, Shield, Crown, GraduationCap, Building2 } from "lucide-react";
import { Badge } from "../../components/ui/badge";

export function getScoreColor(score) {
  if (score >= 80) return "text-[#14B8A6]";
  if (score >= 60) return "text-[#F59E0B]";
  if (score > 0) return "text-red-500";
  return "text-slate-300";
}

export function getScoreRingColor(score) {
  if (score >= 80) return "#14B8A6";
  if (score >= 60) return "#F59E0B";
  return "#EF4444";
}

export function getReadinessConfig(classification) {
  const configs = {
    "field-ready": { label: "Field Ready", color: "bg-[#14B8A6]/10 text-[#14B8A6]" },
    "needs-coaching": { label: "Needs Coaching", color: "bg-[#F59E0B]/10 text-[#F59E0B]" },
  };
  return configs[classification] || { label: "Not Ready", color: "bg-red-50 text-red-500" };
}

export function getRoleIcon(role) {
  if (role === "admin") return <Shield className="w-4 h-4" />;
  if (role === "manager") return <Crown className="w-4 h-4" />;
  return <GraduationCap className="w-4 h-4" />;
}

export function getRoleBadge(role) {
  const badges = { admin: "bg-purple-100 text-purple-700", manager: "bg-[#2563EB]/10 text-[#2563EB]" };
  return badges[role] || "bg-[#14B8A6]/10 text-[#14B8A6]";
}

export function getLanguageLabel(lang) {
  if (lang === "hinglish") return "Hinglish";
  if (lang === "hi") return "Hindi";
  return "English";
}

export function ProfileInfoCard({ profile }) {
  return (
    <div className="p-8 text-center">
      <div className="w-20 h-20 bg-[#0B1F3B] rounded-2xl flex items-center justify-center mx-auto mb-4">
        <User className="w-10 h-10 text-white" />
      </div>
      <h2 className="text-xl font-bold text-[#0B1F3B] mb-1">{profile.name}</h2>
      <p className="text-sm text-slate-400 mb-3">{profile.email}</p>
      <Badge className={`${getRoleBadge(profile.role)} text-sm px-3 py-1`}>
        {getRoleIcon(profile.role)}
        <span className="ml-1.5 capitalize">{profile.role}</span>
      </Badge>
      {profile.team && (
        <div className="mt-4 p-3 bg-slate-50 rounded-xl">
          <div className="flex items-center justify-center gap-2 text-sm text-slate-600">
            <Building2 className="w-4 h-4 text-[#2563EB]" />
            <span className="font-medium">{profile.team.name}</span>
          </div>
        </div>
      )}
      {profile.created_at && (
        <p className="text-xs text-slate-300 mt-4">
          Joined {new Date(profile.created_at).toLocaleDateString("en-IN", { month: "short", year: "numeric" })}
        </p>
      )}
    </div>
  );
}

export function ReadinessScoreCard({ profile }) {
  const circumference = 2 * Math.PI * 45;
  const scoreOffset = circumference - (profile.average_readiness / 100) * circumference;
  const readiness = getReadinessConfig(profile.readiness_classification);

  return (
    <div className="p-8 flex flex-col items-center justify-center text-center">
      <div className="relative w-32 h-32 mb-4">
        <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="45" fill="none" stroke="#E2E8F0" strokeWidth="6" />
          <circle
            cx="50" cy="50" r="45" fill="none"
            stroke={getScoreRingColor(profile.average_readiness)}
            strokeWidth="6" strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={scoreOffset}
            className="score-circle"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-3xl font-bold ${getScoreColor(profile.average_readiness)}`}>
            {profile.average_readiness || 0}
          </span>
          <span className="text-xs text-slate-400">Readiness</span>
        </div>
      </div>
      <Badge className={`${readiness.color} px-3 py-1 text-sm`}>{readiness.label}</Badge>
    </div>
  );
}

export function QuickStatsCard({ profile }) {
  return (
    <div className="p-8 flex flex-col justify-center space-y-6">
      <div>
        <p className="text-sm text-slate-500 mb-1">Total Sessions</p>
        <p className="text-3xl font-bold text-[#0B1F3B]">{profile.total_sessions}</p>
      </div>
      <div>
        <p className="text-sm text-slate-500 mb-1">Evaluated</p>
        <p className="text-3xl font-bold text-[#2563EB]">{profile.evaluated_sessions}</p>
      </div>
      <div>
        <p className="text-sm text-slate-500 mb-1">Avg. Score</p>
        <p className={`text-3xl font-bold ${getScoreColor(profile.average_score)}`}>
          {profile.average_score > 0 ? `${profile.average_score}%` : "\u2014"}
        </p>
      </div>
    </div>
  );
}
